import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Radar } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { OUTBOUND_SETTINGS_URL, OUTBOUND_SUMMARY_URL } from "../../constants/api";
import { OutboundHeader, Pill, Section, Stat } from "./ui";

/**
 * Outbound -> Overview. The funnel from map entry to lead, what is waiting on
 * a person, and which signals actually get replies.
 */

const STEPS = [
    ["Find", "Search OpenStreetMap for hospitals and clinics in a city, or paste a list you have."],
    ["Read", "Their public website is read for facts (beds, specialties, booking, NABH...) and a published email."],
    ["Choose", "Build a cohort from those facts and start a sequence on it."],
    ["Draft", "The AI Worker on your PC writes a 3-email sequence per prospect, using only those facts."],
    ["Approve", "Every email waits in the Outbox. Nothing is sent until someone approves it."],
    ["Send", "Approved emails go from your Settings mailboxes, inside the send window, under the daily cap."],
    ["Replies", "Press Check replies. Interested replies become leads and go to the assignment engine."],
];

export default function OutboundOverview() {
    const { fetch } = useApiMethods();
    const [s, setS] = useState(null);
    const [cfg, setCfg] = useState(null);

    useEffect(() => {
        fetch(OUTBOUND_SUMMARY_URL).then(res => {
            if (res?.status === "success") setS(res.data);
            else ToastMsg("error", res?.message || "Could not load outbound summary");
        });
        fetch(OUTBOUND_SETTINGS_URL).then(res => { if (res?.status === "success") setCfg(res.data); });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const warnings = [];
    if (cfg && !cfg.is_saved) warnings.push(["Outbound is not set up yet.", "/settings/outbound", "Open Settings"]);
    if (cfg && cfg.is_saved && !cfg.is_enabled) warnings.push(["Sending is switched off. Drafts and approvals still work.", "/settings/outbound", "Settings"]);
    if (cfg && !cfg.product_knowledge?.trim()) warnings.push(["No product knowledge yet: the AI drafts will be generic.", "/settings/outbound", "Add it"]);
    if (cfg && cfg.mailboxes?.length === 0) warnings.push(["No mailbox is in the sending pool.", "/email/sending-pool", "Sending Pool"]);
    if (s?.awaiting_drafts) warnings.push([`${s.awaiting_drafts} prospect(s) are waiting for the AI Worker to write their emails. Run it on the PC that has Claude.`, null, null]);
    if (s?.drafts_waiting) warnings.push([`${s.drafts_waiting} email(s) waiting for approval.`, "/outbound/outbox", "Review"]);
    if (s?.blocked) warnings.push([`${s.blocked} email(s) blocked by the pre-send check.`, "/outbound/outbox?status=blocked", "See why"]);

    const pct = (a, b) => (b ? `${Math.round((100 * a) / b)}%` : "–");

    return (
        <ContainerCard>
            <div className="p-4 space-y-5 overflow-auto">
                <OutboundHeader icon={Radar} title="Outbound"
                    subtitle="Find organisations that are not leads yet, write to them with facts from their own website, and turn the ones who answer into leads.">
                    {s && <Pill tone={s.enabled ? "green" : "gray"}>{s.enabled ? "Sending on" : "Sending off"}</Pill>}
                </OutboundHeader>

                {warnings.length > 0 && (
                    <div className="space-y-1.5">
                        {warnings.map(([text, href, cta]) => (
                            <div key={text} className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 dark:border-amber-800/50 bg-amber-50/60 dark:bg-amber-900/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
                                <span className="flex items-center gap-2"><AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />{text}</span>
                                {href && <Link to={href} className="font-medium underline whitespace-nowrap">{cta}</Link>}
                            </div>
                        ))}
                    </div>
                )}

                <div className="grid gap-3 grid-cols-2 md:grid-cols-4 xl:grid-cols-8">
                    <Stat label="Prospects" value={s?.prospects} hint={s ? `${s.already_leads} already leads` : null} />
                    <Stat label="With email" value={s?.with_email} hint={s ? pct(s.with_email, s.prospects) : null} />
                    <Stat label="Reached" value={s?.reached} hint={s ? `${s.emails_sent} emails sent` : null} />
                    <Stat label="Sent today" value={s?.sent_today} hint={cfg ? `cap ${cfg.daily_send_cap}` : null} />
                    <Stat label="Opened" value={s?.opened} hint={s ? pct(s.opened, s.reached) + " of reached" : null} />
                    <Stat label="Replies" value={s?.replies} hint={s ? pct(s.replies, s.reached) + " reply rate" : null} />
                    <Stat label="Positive" value={s?.positive} hint={s ? `${s.converted} became leads` : null} />
                    <Stat label="Opted out" value={s ? s.unsubscribed + s.bounced : null} hint={s ? `${s.unsubscribed} unsub · ${s.bounced} bounced` : null} />
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                    <Section title="Which facts get answers"
                        subtitle="Of the prospects we reached that carry each fact, how many replied with interest. Use it to tune cohorts.">
                        {!s?.by_signal?.length ? (
                            <p className="text-xs text-gray-400">Nothing sent yet.</p>
                        ) : (
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="text-left text-gray-500 border-b border-gray-200 dark:border-gray-700">
                                        <th className="py-1.5 font-medium">Fact</th>
                                        <th className="py-1.5 font-medium text-right">Reached</th>
                                        <th className="py-1.5 font-medium text-right">Positive</th>
                                        <th className="py-1.5 font-medium text-right">Rate</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {s.by_signal.map(r => (
                                        <tr key={r.signal} className="border-b border-gray-100 dark:border-gray-700/60">
                                            <td className="py-1.5 text-gray-800 dark:text-gray-100">{r.label}</td>
                                            <td className="py-1.5 text-right tabular-nums">{r.reached}</td>
                                            <td className="py-1.5 text-right tabular-nums">{r.positive}</td>
                                            <td className="py-1.5 text-right tabular-nums font-medium">{r.rate}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </Section>

                    <Section title="How it works">
                        <ol className="space-y-2 text-xs">
                            {STEPS.map(([head, body], i) => (
                                <li key={head} className="flex gap-3">
                                    <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">{i + 1}</span>
                                    <span className="text-gray-700 dark:text-gray-300"><b className="text-gray-900 dark:text-white">{head}.</b> {body}</span>
                                </li>
                            ))}
                        </ol>
                    </Section>
                </div>
            </div>
        </ContainerCard>
    );
}
